import axios from 'axios'

const apiURL = process.env.REACT_APP_API_URL

export interface reportSchedule {
  reportScheduleId: number
  name: string
  reportKey: string
  reportName: string
  frequency: string
  dayOfWeek: number | null
  dayOfMonth: number | null
  hourOfDay: number
  minuteOfHour: number
  rangeKey: string | null
  rangeName: string | null
  isActive: boolean
  recipientEmployeeIds: number[]
  recipientGroupIds: number[]
  /** Who it goes to, in words. "Me" when nobody else is named. */
  recipientSummary: string
  lastRunDate: string | null
  nextRunDate: string | null
  lastStatus: string | null
  /** Plain English recurrence, built server side. */
  summary: string
}

export interface reportScheduleOption {
  key: string
  displayName: string
  /** False for snapshot reports, which have no period to pick. */
  needsDateRange: boolean
}

export interface reportScheduleRecipientOption {
  id: number
  name: string
}

export interface reportScheduleOptions {
  reports: reportScheduleOption[]
  ranges: reportScheduleOption[]
  frequencies: string[]
  deliveryEmail: string | null
  /** Administrators only. False hides the recipient pickers entirely. */
  canChooseRecipients: boolean
  employees: reportScheduleRecipientOption[]
  groups: reportScheduleRecipientOption[]
}

export interface reportScheduleForm {
  name: string
  reportKey: string
  frequency: string
  dayOfWeek: number | null
  dayOfMonth: number | null
  hourOfDay: number
  minuteOfHour: number
  rangeKey: string | null
  isActive: boolean
  /** Administrators only; the API refuses these from anybody else. */
  recipientEmployeeIds: number[]
  recipientGroupIds: number[]
}

const authHeaders = () => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  return {
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + token,
  }
}

export const getReportSchedules = async (): Promise<reportSchedule[]> => {
  const response = await axios.get<reportSchedule[]>(
    `https://${apiURL}/ReportSchedule`,
    { headers: authHeaders() }
  )

  return response.data
}

export const getReportScheduleOptions = async (): Promise<reportScheduleOptions> => {
  const response = await axios.get<reportScheduleOptions>(
    `https://${apiURL}/ReportSchedule/Options`,
    { headers: authHeaders() }
  )

  return response.data
}

export const createReportSchedule = async (form: reportScheduleForm): Promise<reportSchedule> => {
  const response = await axios.post<reportSchedule>(
    `https://${apiURL}/ReportSchedule`,
    form,
    { headers: authHeaders() }
  )

  return response.data
}

export const updateReportSchedule = async (
  id: number,
  form: reportScheduleForm
): Promise<reportSchedule> => {
  const response = await axios.put<reportSchedule>(
    `https://${apiURL}/ReportSchedule/${id}`,
    form,
    { headers: authHeaders() }
  )

  return response.data
}

export const deleteReportSchedule = async (id: number): Promise<void> => {
  await axios.delete(`https://${apiURL}/ReportSchedule/${id}`, { headers: authHeaders() })
}

/** Sends one schedule now, leaving its recurrence where it was. */
export const sendReportScheduleNow = async (id: number): Promise<string> => {
  const response = await axios.post<{ message: string }>(
    `https://${apiURL}/ReportSchedule/${id}/Send`,
    {},
    { headers: authHeaders() }
  )

  return response.data.message
}
